import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    Camera,
    FileText,
    Check,
    Upload,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { CITIES, COUNTRIES } from "@/constants/patient";
import { TID } from "@/constants/testIds";

const TITLES = ["Dr.", "Vaidya", "Acharya", "BAMS", "MD Ayurveda"];
const DEGREES = [
    "BAMS",
    "MD Ayurveda",
    "MS Ayurveda",
    "PhD Ayurveda",
    "BAMS + PG Diploma",
    "Other",
];
const DOC_ACCEPT =
    "application/pdf,image/jpeg,image/jpg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function DocUploader({ label, value, onChange, testId, hint }) {
    const ref = useRef(null);
    const hasFile = Boolean(value?.data);
    async function onPick(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("File too large. Please keep under 5 MB.");
            return;
        }
        const dataUrl = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsDataURL(file);
        });
        onChange({ data: dataUrl, name: file.name, size: file.size, type: file.type });
    }
    return (
        <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                {label} <span className="text-veda-error">*</span>
            </p>
            <input
                ref={ref}
                type="file"
                accept={DOC_ACCEPT}
                className="hidden"
                onChange={onPick}
            />
            {hasFile ? (
                <div className="mt-1 flex items-center justify-between border border-veda-brand/40 bg-veda-brand/5 p-3">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center bg-veda-brand text-white">
                            <Check size={14} />
                        </span>
                        <div className="min-w-0">
                            <p className="truncate text-xs font-semibold text-veda-onSurface">
                                {value.name}
                            </p>
                            <p className="text-[10px] text-veda-muted">
                                {(value.size / 1024).toFixed(0)} KB · {value.type || "file"}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => ref.current?.click()}
                        data-testid={`${testId}-replace`}
                        className="border border-veda-border bg-veda-surface px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                    >
                        Replace
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => ref.current?.click()}
                    data-testid={testId}
                    className="mt-1 flex w-full items-center justify-center gap-2 border border-dashed border-veda-border bg-veda-surface2 p-6 text-xs text-veda-muted hover:border-veda-brand"
                >
                    <Upload size={13} />
                    Upload PDF / JPG / PNG / DOC (max 5 MB)
                </button>
            )}
            {hint && (
                <p className="mt-1.5 text-[10px] text-veda-muted">{hint}</p>
            )}
        </div>
    );
}

const inputCls =
    "border border-veda-border bg-veda-surface px-3 py-2.5 text-sm text-veda-onSurface focus:border-veda-brand focus:outline-none";

export default function RegisterDoctor() {
    const nav = useNavigate();
    const { registerDoctor } = useAuth();
    const avatarRef = useRef(null);
    const [f, setF] = useState({
        name: "",
        email: "",
        password: "",
        phone: "",
        phone_country: "IN",
        title: "Dr.",
        specialty: "",
        experience_years: "",
        qualifications: "",
        degree_name: "BAMS",
        clinic_name: "",
        clinic_address: "",
        practice_locations: [],
        license_number: "",
        bio: "",
        consultation_fee: "500",
        avatar_url: "",
    });
    const [degreeDoc, setDegreeDoc] = useState(null);
    const [licenseDoc, setLicenseDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const dial = useMemo(
        () => COUNTRIES.find((c) => c.code === f.phone_country)?.dial || "+",
        [f.phone_country],
    );

    function set(k, v) {
        setF((prev) => ({ ...prev, [k]: v }));
    }

    function toggleLocation(city) {
        setF((prev) => ({
            ...prev,
            practice_locations: prev.practice_locations.includes(city)
                ? prev.practice_locations.filter((x) => x !== city)
                : [...prev.practice_locations, city],
        }));
    }

    function onPickAvatar(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Photo too large. Under 3 MB please.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => set("avatar_url", reader.result);
        reader.readAsDataURL(file);
    }

    async function submit(e) {
        e.preventDefault();
        setErr("");
        if (!f.phone.trim()) return setErr("Phone number is required.");
        if (!degreeDoc?.data) return setErr("Please upload your degree document.");
        if (!licenseDoc?.data) return setErr("Please upload your practice license document.");
        if (!f.license_number.trim()) return setErr("License number is required.");
        if (!f.clinic_name.trim()) return setErr("Clinic name is required.");
        if (!f.clinic_address.trim()) return setErr("Clinic address is required.");
        if (f.practice_locations.length === 0)
            return setErr("Select at least one practice location.");
        if (Number(f.experience_years) < 0 || !f.experience_years)
            return setErr("Enter valid years of experience.");
        setLoading(true);
        try {
            await registerDoctor({
                name: f.name.trim(),
                email: f.email.trim(),
                password: f.password,
                phone: `${dial} ${f.phone.trim()}`,
                title: f.title,
                specialty: f.specialty.trim(),
                experience_years: Number(f.experience_years),
                qualifications: f.qualifications.trim(),
                degree_name: f.degree_name,
                degree_document: degreeDoc.data,
                license_number: f.license_number.trim(),
                license_document: licenseDoc.data,
                clinic_name: f.clinic_name.trim(),
                clinic_address: f.clinic_address.trim(),
                practice_locations: f.practice_locations,
                bio: f.bio.trim(),
                consultation_fee: Number(f.consultation_fee) || 500,
                avatar_url: f.avatar_url || undefined,
            });
            toast.success("Application submitted. Our team will verify shortly.");
            nav("/doctor/pending");
        } catch (e2) {
            const detail = e2?.response?.data?.detail;
            setErr(typeof detail === "string" ? detail : "Please review the form and try again.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div
            data-testid={TID.doctorRegisterScreen}
            className="veda-container-narrow py-10 md:py-16"
        >
            <div className="mx-auto max-w-3xl">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                >
                    <ArrowLeft size={14} /> Home
                </Link>
                <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                    Practitioner application
                </p>
                <h1 className="font-display mt-1 text-3xl text-veda-onSurface md:text-4xl">
                    Join as a Vaidya
                </h1>
                <p className="mt-2 text-sm text-veda-muted">
                    Sushen Herbs manually verifies every practitioner. You'll
                    log in to a pending screen while our team reviews your
                    credentials — usually within 48 hours.
                </p>

                <form onSubmit={submit} className="mt-8 flex flex-col gap-6">
                    {/* Identity */}
                    <section>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Identity
                        </p>
                        <div className="mt-3 flex items-center gap-3">
                            <div className="h-16 w-16 shrink-0 overflow-hidden border border-veda-border bg-veda-surface2">
                                {f.avatar_url ? (
                                    <img
                                        src={f.avatar_url}
                                        alt=""
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="grid h-full w-full place-items-center text-veda-muted">
                                        <Camera size={18} />
                                    </div>
                                )}
                            </div>
                            <div>
                                <button
                                    type="button"
                                    onClick={() => avatarRef.current?.click()}
                                    className="border border-veda-border bg-veda-surface px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                                >
                                    {f.avatar_url ? "Change photo" : "Upload photo"}
                                </button>
                                <input
                                    ref={avatarRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={onPickAvatar}
                                    className="hidden"
                                />
                                <p className="mt-1 text-[10px] text-veda-muted">
                                    JPG / PNG under 3 MB
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                            <div>
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                    Title
                                </p>
                                <select
                                    value={f.title}
                                    onChange={(e) => set("title", e.target.value)}
                                    data-testid={TID.docregTitle}
                                    className={inputCls + " w-full"}
                                >
                                    {TITLES.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <input
                                required
                                placeholder="Full name"
                                value={f.name}
                                onChange={(e) => set("name", e.target.value)}
                                data-testid={TID.docregName}
                                className={inputCls}
                            />
                            <input
                                required
                                type="email"
                                placeholder="Email"
                                value={f.email}
                                onChange={(e) => set("email", e.target.value)}
                                data-testid={TID.docregEmail}
                                className={inputCls}
                            />
                            <input
                                required
                                type="password"
                                placeholder="Password (min 6 chars)"
                                minLength={6}
                                value={f.password}
                                onChange={(e) => set("password", e.target.value)}
                                data-testid={TID.docregPassword}
                                className={inputCls}
                            />
                            <div className="flex gap-2">
                                <select
                                    value={f.phone_country}
                                    onChange={(e) => set("phone_country", e.target.value)}
                                    className={inputCls}
                                >
                                    {COUNTRIES.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.code} {c.dial}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    required
                                    type="tel"
                                    placeholder="Phone number"
                                    value={f.phone}
                                    onChange={(e) => set("phone", e.target.value)}
                                    data-testid={TID.docregPhone}
                                    className={inputCls + " flex-1"}
                                />
                            </div>
                            <input
                                required
                                placeholder="Specialty (e.g. Panchakarma, Prakriti Analyst)"
                                value={f.specialty}
                                onChange={(e) => set("specialty", e.target.value)}
                                data-testid={TID.docregSpecialty}
                                className={inputCls}
                            />
                        </div>
                    </section>

                    {/* Credentials */}
                    <section>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Credentials
                        </p>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                            <div>
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                    Degree name
                                </p>
                                <select
                                    value={f.degree_name}
                                    onChange={(e) => set("degree_name", e.target.value)}
                                    data-testid={TID.docregDegreeName}
                                    className={inputCls + " w-full"}
                                >
                                    {DEGREES.map((d) => (
                                        <option key={d} value={d}>
                                            {d}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <input
                                required
                                type="number"
                                min={0}
                                placeholder="Years of experience"
                                value={f.experience_years}
                                onChange={(e) => set("experience_years", e.target.value)}
                                data-testid={TID.docregExperience}
                                className={inputCls}
                            />
                            <input
                                required
                                placeholder="Qualifications summary (e.g. BAMS, MD Ayurveda, PG Diploma Panchakarma)"
                                value={f.qualifications}
                                onChange={(e) => set("qualifications", e.target.value)}
                                data-testid={TID.docregQualifications}
                                className={inputCls + " md:col-span-2"}
                            />
                            <input
                                required
                                placeholder="Practice license number"
                                value={f.license_number}
                                onChange={(e) => set("license_number", e.target.value)}
                                data-testid={TID.docregLicense}
                                className={inputCls}
                            />
                            <input
                                type="number"
                                min={0}
                                placeholder="Consultation fee (₹)"
                                value={f.consultation_fee}
                                onChange={(e) => set("consultation_fee", e.target.value)}
                                className={inputCls}
                            />
                        </div>

                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                            <DocUploader
                                label="Degree document"
                                value={degreeDoc}
                                onChange={setDegreeDoc}
                                testId={TID.docregDegreeDoc}
                                hint="Attach your BAMS or MD certificate for admin verification."
                            />
                            <DocUploader
                                label="Practice license document"
                                value={licenseDoc}
                                onChange={setLicenseDoc}
                                testId={TID.docregLicenseDoc}
                                hint="Scanned certificate from your state Ayurveda council."
                            />
                        </div>
                    </section>

                    {/* Clinic */}
                    <section>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Clinic
                        </p>
                        <div className="mt-3 flex flex-col gap-3">
                            <input
                                required
                                placeholder="Clinic name (e.g. Nalanda Ayurveda Kendra)"
                                value={f.clinic_name}
                                onChange={(e) => set("clinic_name", e.target.value)}
                                data-testid={TID.docregClinicName}
                                className={inputCls}
                            />
                            <textarea
                                required
                                rows={2}
                                placeholder="Clinic address"
                                value={f.clinic_address}
                                onChange={(e) => set("clinic_address", e.target.value)}
                                data-testid={TID.docregClinicAddress}
                                className={inputCls}
                            />
                        </div>
                    </section>

                    {/* Practice locations */}
                    <section>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Practice locations <span className="text-veda-error">*</span>
                        </p>
                        <p className="mt-1 text-xs text-veda-muted">
                            Tap every city where you consult (in person or
                            online).
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {CITIES.filter((c) => c !== "Other").map((c) => {
                                const active = f.practice_locations.includes(c);
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => toggleLocation(c)}
                                        data-testid={TID.docregLocationChip(
                                            c.replace(/\s+/g, "-").toLowerCase(),
                                        )}
                                        className={`inline-flex items-center gap-1 rounded-pill border px-3 py-1.5 text-[11px] font-semibold ${
                                            active
                                                ? "border-veda-brand bg-veda-brand text-white"
                                                : "border-veda-border bg-veda-surface text-veda-onSurface"
                                        }`}
                                    >
                                        {active && <Check size={11} />}
                                        {c}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <textarea
                        rows={3}
                        placeholder="Short bio (optional) — what patients can expect from you"
                        value={f.bio}
                        onChange={(e) => set("bio", e.target.value)}
                        className={inputCls}
                    />

                    {err && (
                        <p
                            data-testid={TID.docregError}
                            className="text-xs text-veda-error"
                        >
                            {err}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid={TID.docregSubmit}
                        className="inline-flex items-center justify-center gap-2 bg-veda-brand px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-white disabled:opacity-70"
                    >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        Submit application for verification
                    </button>
                    <p className="text-center text-[10px] text-veda-muted">
                        Your uploaded documents are visible only to the Sushen
                        Herbs admin team.
                    </p>
                </form>
            </div>
        </div>
    );
}
